import React, { useEffect, useState } from 'react';
import { getAllEmailLogs, getEmailStatistics, cleanupOldLogs } from '../services/emailLogs';

const EmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('logs');
  const [filters, setFilters] = useState({
    status: '',
    limit: 50
  });

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else {
      fetchStatistics();
    }
  }, [activeTab, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.limit) params.limit = filters.limit;
      
      const response = await getAllEmailLogs(params);
      setLogs(response.logs || response.data || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      alert('Failed to load email logs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await getEmailStatistics();
      setStatistics(response.statistics || response.data || null);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      alert('Failed to load statistics: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    const days = prompt('Delete logs older than how many days?', '30');
    if (!days) return;
    
    if (!window.confirm(`Delete all logs older than ${days} days?`)) return;

    try {
      await cleanupOldLogs(parseInt(days));
      alert('Old logs cleaned successfully!');
      fetchLogs();
    } catch (error) {
      alert('Failed to cleanup logs: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'bounced':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">Email Logs & Statistics</h1>
        {activeTab === 'logs' && (
          <button
            onClick={handleCleanup}
            className="bg-red-600 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-red-700 text-sm md:text-base w-full sm:w-auto"
          >
            Cleanup Old Logs
          </button>
        )}
      </div>

      <div className="mb-6 flex gap-2 md:gap-4 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'logs' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Email Logs
        </button>
        <button
          onClick={() => setActiveTab('statistics')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'statistics' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Statistics
        </button>
      </div>

      {activeTab === 'logs' && (
        <>
          <div className="mb-4 flex flex-col sm:flex-row gap-2 md:gap-4">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="border rounded px-3 py-2 text-sm md:text-base w-full sm:w-auto"
            >
              <option value="">All Status</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
              <option value="bounced">Bounced</option>
            </select>

            <select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: e.target.value })}
              className="border rounded px-3 py-2 text-sm md:text-base w-full sm:w-auto"
            >
              <option value="50">50 records</option>
              <option value="100">100 records</option>
              <option value="200">200 records</option>
              <option value="500">500 records</option>
            </select>

            <button
              onClick={fetchLogs}
              className="bg-blue-600 text-white px-3 md:px-4 py-2 rounded hover:bg-blue-700 text-sm md:text-base w-full sm:w-auto"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading logs...</div>
          ) : (
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Recipient</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Subject</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Sent At</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                          No email logs found
                        </td>
                      </tr>
                    ) : (
                      logs.map((log) => (
                        <tr key={log.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{log.recipient_email}</td>
                          <td className="px-4 py-3 text-sm">{log.subject}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs ${getStatusColor(log.status)}`}>
                              {log.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {log.sent_at ? new Date(log.sent_at).toLocaleString() : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-red-600">
                            {log.error_message || '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'statistics' && (
        <div>
          {loading ? (
            <div className="text-center py-12">Loading statistics...</div>
          ) : statistics ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Emails</p>
                    <p className="text-3xl font-bold mt-2">{statistics.total_emails || 0}</p>
                  </div>
                  <div className="text-4xl">📧</div>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Successfully Sent</p>
                    <p className="text-3xl font-bold mt-2 text-green-600">{statistics.sent_count || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {statistics.total_emails > 0 
                        ? `${((statistics.sent_count / statistics.total_emails) * 100).toFixed(1)}%` 
                        : '0%'}
                    </p>
                  </div>
                  <div className="text-4xl">✅</div>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Failed</p>
                    <p className="text-3xl font-bold mt-2 text-red-600">{statistics.failed_count || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {statistics.total_emails > 0 
                        ? `${((statistics.failed_count / statistics.total_emails) * 100).toFixed(1)}%` 
                        : '0%'}
                    </p>
                  </div>
                  <div className="text-4xl">❌</div>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Bounced</p>
                    <p className="text-3xl font-bold mt-2 text-orange-600">{statistics.bounced_count || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {statistics.total_emails > 0 
                        ? `${((statistics.bounced_count / statistics.total_emails) * 100).toFixed(1)}%` 
                        : '0%'}
                    </p>
                  </div>
                  <div className="text-4xl">⚠️</div>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Unique Recipients</p>
                    <p className="text-3xl font-bold mt-2">{statistics.unique_recipients || 0}</p>
                  </div>
                  <div className="text-4xl">👥</div>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Success Rate</p>
                    <p className="text-3xl font-bold mt-2 text-blue-600">
                      {statistics.total_emails > 0 
                        ? `${((statistics.sent_count / statistics.total_emails) * 100).toFixed(1)}%` 
                        : '0%'}
                    </p>
                  </div>
                  <div className="text-4xl">📊</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No statistics available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailLogs;
